import { reactive } from 'vue'
import type { DeepWritable } from 'ts-essentials'
export type FeatureFlagKey = (typeof featureFlags)[number]['featureKey']

interface FeatureFlagDefinition {
  /**
   * Used for the employee settings UI.  If empty, no setting will be provided.
   */
  label: string

  /**
   * The split key key.
   */
  featureKey: string

  /**
   * If the flag is enabled via Split.
   * Can be a string if mutlivariate split is used.
   */
  splitEnabled?: boolean | string | null

  /**
   * If the flag is enabled via local storage.
   */
  localEnabled?: boolean | string | null

  /**
   * Variant options for multi-variant split types
   * Defines options presented in the employee settings menu. If not defined, defaults to split config.
   */
  variants?: Readonly<string[]>

  /**
   * If true, this feature will not be checked against split - it will only read local config.
   * Useful if the feature will only be used to gate, not segment.
   * If not present or false, the feature will prefer local setting but will check split if not available.
   */
  localOnly?: boolean

  /**
   * What team owns the flag.
   */
  team: string
}

/**
 * Subset of the FeatureFlagDefinition type that utilizes a union for the
 * property of featureKey - this allows the return of getFlags
 * to have a type narrower than `string` and enables intellisense when a defined feature flag
 * is desired by using the FeatureFlag or FeatureFlagKey type.
 */
export interface FeatureFlag extends FeatureFlagDefinition {
  featureKey: FeatureFlagKey
}

/**
 * Array of feature flags.
 *
 * Required minimum config:
 *
 * ```ts
 *   {
 *     label: '',
 *     featureKey: '',
 *     team: ''
 *   }
 * ```
 *
 * if a feature will not utilize Split, include the `localOnly` property with a value of `true`
 *
 */
const featureFlags = [
  {
    label: 'Personalized Imagery',
    featureKey: 'gd_web_as_personalized_imagery',
    team: 'growth',
  },
  {
    label: 'Full Autosubscribe',
    featureKey: 'gd_disable_onx_trial',
    splitEnabled: null,
    localEnabled: null,
    team: 'growth',
  },
  {
    label: 'Tooltip Bubbles',
    featureKey: 'cj_web_tooltip_bubbles',
    team: 'growth',
  },
  {
    label: 'Offline Maps Expandable Upsell',
    featureKey: 'gd_web_offline_expandable_upsell',
    team: 'growth',
  },
  {
    label: 'Multi-subscription membership card (OxR/SOS)',
    featureKey: 'gd_web_manage_subs',
    team: 'growth',
  },
  {
    label: 'Debug Onboarding',
    featureKey: 'debug_onboarding',
    localOnly: true,
    team: 'growth',
  },
  /**
   * Force skip onboarding request / initialization.
   */
  {
    label: 'Skip Onboarding',
    featureKey: 'skip_onboarding',
    localOnly: true,
    team: 'growth',
  },
  {
    label: 'Debug Onboarding Tooltips',
    featureKey: 'debug_onboarding_tooltips',
    localOnly: true,
    team: 'growth',
  },
  {
    label: 'Debug Promotional Offers',
    featureKey: 'debug_promotional_offers',
    localOnly: true,
    team: 'growth',
  },
  {
    label: 'Onboarding A/A Test',
    featureKey: 'gd_web_aa_onboarding',
    team: 'growth',
  },
  {
    label: 'Bypass Markup Count Import Limit',
    featureKey: 'ccc_markups_no_import_limit',
    team: 'fox',
  },
  {
    label: 'Enable Right Click Debugging Menu',
    featureKey: 'enable_debugging_menu',
    localOnly: true,
    team: 'web',
  },
  {
    label: 'Identity Account Management',
    featureKey: 'core_identity_account_management',
    team: 'core',
  },
  {
    label: 'Debug Identity client in console as id_client',
    featureKey: 'debug_identity_client',
    localOnly: true,
    team: 'core',
  },
  {
    label: 'Layer Management Service - Hunt Layers',
    featureKey: 'mx_layer_management_service',
    team: 'content',
  },
  {
    label: 'Static Content Share Urls',
    featureKey: 'cxn_share_static_content_query_location_url',
    team: 'fox',
  },
  {
    label: 'Initial Viewport Based Markups Request',
    featureKey: 'fox_viewport_markup_load',
    team: 'fox',
  },
  {
    label: 'Alternative Map Viewer',
    featureKey: 'map_x_viewer',
    splitEnabled: false,
    localEnabled: false,
    variants: ['Onyx'],
    localOnly: false,
    team: 'core',
  },
  {
    label: 'Debug Map Viewer',
    featureKey: 'map_x_viewer_debug',
    splitEnabled: false,
    localEnabled: false,
    localOnly: true,
    team: 'core',
  },
  {
    label: 'Telemetry',
    featureKey: 'content_user_pan_zoom',
    team: 'content',
  },
  {
    label: 'Offline Maps Allow All Detail Levels',
    featureKey: 'core_offline_maps_allow_all_detail_levels',
    localOnly: true,
    team: 'core',
  },
  {
    label: 'Waypoint Clustering',
    featureKey: 'fox_waypoint_clustering',
    team: 'fox',
  },
  {
    label: 'Debug Dark Mode',
    featureKey: 'core_debug_dark_mode',
    team: 'core',
    localOnly: true,
  },
  {
    label: 'Mobile App Testbed',
    featureKey: 'prototype_fox_mobile_testbed',
    variants: ['long_press_waypoint', 'long_press_menu'],
    team: 'prototype',
  },
  {
    label: 'Map Dots & Pins',
    featureKey: 'prototype_fox_waypoint_pins',
    team: 'prototype',
  },
  {
    label: 'Explore UX',
    featureKey: 'prototype_fox_explore',
    team: 'prototype',
  },
  {
    label: 'Map Markup Bulk Select',
    featureKey: 'prototype_fox_map_markup_bulk_select',
    team: 'prototype',
  },
  {
    label: 'Fish supported states',
    featureKey: 'fish_midwest_supported__states',
    team: 'fish',
  },
  {
    label: 'Remove Fish Supported Mask',
    featureKey: 'remove_mask',
    localOnly: true,
    team: 'fish',
  },
  {
    label: 'Search Autocomplete',
    featureKey: 'fox_web_autocomplete',
    team: 'fox',
  },
  {
    label: 'Search Pins v2',
    featureKey: 'fox_search_pins_v2',
    team: 'fox',
  },
  {
    label: 'Content Collections Package',
    featureKey: 'fox_web_collections_package',
    team: 'fox',
  },
  {
    label: 'Search Package',
    featureKey: 'fox_web_search_package',
    team: 'fox',
  },
  {
    label: 'onX Guide',
    featureKey: 'ai_guide_chat',
    team: 'ai 🤖',
  },
  {
    label: 'CPUE Updates',
    featureKey: 'cpue_timeseries',
    team: 'fish',
  },
  {
    label: 'Fishing Reports',
    featureKey: 'fishing_reports_web',
    team: 'fish',
  },
  {
    label: 'HD Contours',
    featureKey: 'fish_hd_contours',
    team: 'fish',
  },
] as const satisfies Readonly<FeatureFlagDefinition[]>

// Reactive array of feature flags
const featureFlagState: FeatureFlag[] = reactive(
  featureFlags as DeepWritable<typeof featureFlags>
)

/**
 * *(AI-generated)*
 * Returns the reactive array of all feature flag definitions.
 */
export function getFlags() {
  return featureFlagState
}

/**
 * *(AI-generated)*
 * Returns an array of all feature flag keys.
 */
export const getFlagKeys = () => {
  return featureFlagState.map((f) => f.featureKey)
}
